import React, { useEffect } from 'react';
import NavBar from '../../components/NavBar';
import ResponsiveFooter from '../../components/ResponsiveFooter';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

// Apple-inspired color palette (matching LandingPage.jsx)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#6e6e6e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles (matching LandingPage.jsx)
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#000000',
  },
  subheading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#333333',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
    color: '#333333',
  },
  medium: {
    fontWeight: 500,
  }
};

// Animation variants
const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6,
      ease: [0.6, -0.05, 0.01, 0.99]
    } 
  }
};

const TermsOfService = () => {

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col"
      style={{
        color: colors.gray900,
        fontFamily: fontStyles.fontFamily
      }}>
      {/* Navigation */}
      <NavBar 
        activePage=""
      />
      
      {/* Content */}
      <motion.div 
        className="relative py-20 overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={fadeInVariants}
        style={{
          fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
          background: 'linear-gradient(to bottom, white, #f8faff)'
        }}
      >
        {/* Vertical Rails - Aligned with Footer positioning */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <div className="max-w-7xl mx-auto h-full relative" style={{ paddingLeft: 24, paddingRight: 24 }}>
            {/* Left vertical lines - slightly darker */}
            <div className="absolute left-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute left-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
            {/* Right vertical lines - slightly darker */}
            <div className="absolute right-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute right-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
          </div>
        </div>
        
        <div className="container mx-auto px-6 pt-12 pb-0 relative z-10">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center mb-8 text-sm hover:text-blue-600 transition"
            style={{
              color: colors.gray600,
              fontWeight: 500,
              letterSpacing: '-0.01em'
            }}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Link>
          
          <div className="prose prose-lg max-w-none">
            <div className="flex flex-col items-start mb-6">
              <img src="/6.png" alt="SignPact Logo" className="h-12 mb-4" />
              <h1 className="text-4xl font-medium tracking-normal" 
                style={{
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 500,
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  marginTop: '0.25rem'
                }}>
                Terms of Service.
              </h1>
            </div>
            
            <p className="text-sm italic mb-8" 
              style={{
                color: colors.gray500,
                fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 400,
                letterSpacing: '-0.01em'
              }}>
              Last Updated: June 12, 2025
            </p>
            
            <p className="mb-6" style={fontStyles.body}>
              Welcome to SignPact! These Terms of Service ("Terms") govern your access to and use of <strong style={{fontWeight: 500}}>SignPact</strong>, an AI-powered electronic signature and contract lifecycle management platform. SignPact, Inc. ("SignPact," "we," "us," or "our") provides an online service that allows users to upload and manage contracts, negotiate changes (redlining), obtain electronic signatures, analyze document content, manage vendor relationships, and handle invoicing and related contract workflows. By using the SignPact website and services (collectively, the "Service"), you agree to be bound by these Terms.
            </p>
            
            <p className="mb-10" style={fontStyles.body}>
              SignPact, Inc. is a Delaware C Corporation (EIN: 39-2704088) incorporated on June 12, 2025, and headquartered at 1349 Hinman, Hanover, NH 03755. These Terms constitute a binding legal agreement between you (the user of our Service) and SignPact, Inc. We encourage you to read these Terms carefully. If you <strong style={{fontWeight: 500}}>do not agree</strong> with any part of these Terms, you must not use the Service.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              1. Acceptance of Terms and Eligibility
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>1.1. Eligibility:</strong> You must be legally capable of entering into a binding contract to use SignPact. This means you must be at least 18 years old (or the age of majority in your jurisdiction) and have full legal capacity. By using the Service, you represent and warrant that you meet these eligibility requirements. If you are using SignPact on behalf of a company or other legal entity (for example, as an employee or contractor of a business), you represent and warrant that you have the authority to bind that entity to these Terms. In that case, "you" and "your" will refer to both you as an individual and the entity you represent.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>1.2. Account Registration:</strong> To access most features of SignPact, you will need to create an account. You agree to provide true, accurate, current, and complete information about yourself as prompted by the registration process (such as your name, a valid email address, and any required organizational information). You also agree to update such information promptly if it changes, so we have up-to-date records.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>1.3. Account Security:</strong> You are responsible for maintaining the confidentiality of your account login credentials (username and password). You should use a strong and unique password and keep it secure. Do not share your account credentials with anyone else. You are responsible for all activities that occur under your account, whether or not you authorized them. If you believe your account has been compromised or accessed without authorization, you must notify us immediately at founder@signpact.ai. We are not liable for any loss or damage arising from unauthorized use of your account (but you may be liable to us or others for such unauthorized use).
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>1.4. Acceptance of Terms:</strong> By creating an account or otherwise accessing or using the Service, you acknowledge that you have read, understood, and agree to these Terms, as well as our Privacy Policy (which is incorporated into these Terms by reference). If you do not agree, you should not create an account or use the Service.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>1.5. Updates to Terms:</strong> SignPact may modify these Terms from time to time. We will provide notice of changes as described in Section 13 (Changes to Terms). Continued use of the Service after updated Terms go into effect constitutes your acceptance of the changes. The current version of the Terms will always be accessible through the Service, and we encourage you to review them periodically.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              2. Description of the Service
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              SignPact provides a suite of tools to manage the entire lifecycle of contracts and agreements:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Document Upload and Storage:</strong> You can upload contracts and related documents to the platform. SignPact will securely store these documents and allow you to organize them (e.g., by project, counterparty, or vendor).
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Collaboration and Redlining:</strong> SignPact enables multiple parties to collaborate on a document. You can propose changes (redlines), accept or reject changes, leave comments, and manage versions of a contract as it goes through negotiation. All changes and comments are tracked with user attribution and timestamps.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Electronic Signatures:</strong> The Service allows you to send documents for electronic signature and to sign documents electronically. Each signer can review the document and apply a legally-binding e-signature. SignPact records an audit trail for signature events (including who signed and when).
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Document Analytics (AI-Powered):</strong> We offer AI-driven analysis of contracts. For example, the Service might highlight key clauses, summarize sections, or identify potential issues in a contract (for informational purposes).
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Vendor Management:</strong> You can catalog and manage contracts by vendor or partner. This includes tracking contract dates (execution, renewal, expiration), storing contact information for counterparties, and receiving alerts or reports on vendor-related contract metrics.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Invoicing and Payment Tracking:</strong> The Service may allow you to generate invoices related to contracts (for instance, if you finalize a contract with payment terms, you can create an invoice for the agreed amount).
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              3. User Content and Ownership
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>3.1. Your Content:</strong> In using SignPact, you will be uploading, creating, or sharing content such as contract documents, attachments, text, images, comments, and other data ("User Content"). All User Content that you upload or generate is <strong style={{fontWeight: 500}}>yours</strong>. SignPact does not claim any ownership rights in your User Content. These Terms do not grant us any ownership over your content; rather, you grant us certain rights to use your content solely as needed to provide the Service, as described below.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>3.2. License to SignPact:</strong> When you upload or submit User Content to our Service, you grant SignPact a worldwide, non-exclusive, royalty-free, <strong style={{fontWeight: 500}}>limited license</strong> to host, store, process, transmit, and display that content <strong style={{fontWeight: 500}}>for the purpose of operating and facilitating the Service</strong>.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>3.3. Your Responsibilities for Content:</strong> You are solely responsible for all User Content you upload or create on SignPact. You represent and warrant that you have all necessary rights, permissions, and authority to upload the content to the Service and to grant the license in Section 3.2.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>3.4. Content Visibility and Sharing:</strong> By default, content you upload is private to your account unless you choose to share it. When you invite others to view, collaborate on, or sign a document, you understand that those individuals will have access to the content of that document and any related information.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>3.5. Removal of Content:</strong> You can delete your User Content from the Service at any time. Additionally, SignPact reserves the right to remove or disable access to any User Content at our discretion, for example if we believe it violates these Terms or applicable law.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>3.6. Backup Your Data:</strong> While we maintain backups and redundancy, we cannot guarantee that User Content will never be lost or corrupted. <strong style={{fontWeight: 500}}>We strongly encourage you to keep copies of your important documents and data outside of SignPact as well.</strong>
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              4. Acceptable Use Policy
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              To ensure a safe and reliable Service for all users, you agree to abide by the following rules and not to misuse the SignPact Service. In particular, <strong style={{fontWeight: 500}}>you agree that you will NOT:</strong>
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>4.1. Illegal or Harmful Conduct:</strong> Use the Service for any unlawful purpose or in furtherance of illegal activities. This includes, but is not limited to, uploading or managing contracts that involve illegal subject matter or activities.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>4.2. Infringing Content:</strong> Upload, post, or transmit any content that infringes or violates the intellectual property rights or any other rights of anyone else.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>4.3. Prohibited Content:</strong> Upload or share any content that is defamatory, libelous, obscene, pornographic, indecent, harassing, threatening, or otherwise objectionable.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>4.4. Malicious Code:</strong> Introduce viruses, trojans, worms, logic bombs, or any other malware or harmful material into the Service.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>4.5. Security Interference:</strong> Attempt to interfere with or disrupt the integrity or proper functioning of the Service.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>4.6. Overburdening or Misusing the Service:</strong> Do not use any automated system to send more request messages to the SignPact servers than a human could reasonably produce in the same timeframe.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              5. Intellectual Property Rights
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>5.1. Our Proprietary Rights:</strong> Aside from your User Content, all content and materials available on or comprising the Service are the property of SignPact or our licensors, and are protected by intellectual property laws.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>5.2. License to Use the Service:</strong> Subject to your compliance with these Terms, SignPact grants you a limited, revocable, non-exclusive, non-transferable license to access and use the Service for your internal business or personal purposes.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>5.3. Open Source Components:</strong> We want to be transparent that SignPact is built using open-source libraries and frameworks, such as React, Node.js, Lodash, core-js, and others. Each of these components is subject to its own license which grants rights to you and us to use that code.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>5.4. Feedback:</strong> We welcome feedback, ideas, or suggestions ("Feedback") about how to improve SignPact. If you provide Feedback to us, you agree that we are free to use the Feedback however we choose, without any restriction or obligation to you.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              6. Electronic Signatures and Records
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              SignPact provides functionality for creating legally binding electronic signatures on documents. <strong style={{fontWeight: 500}}>By using the e-signature features of our Service, you agree to the following:</strong>
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>6.1. Legal Validity of E-Signatures:</strong> You intend that your electronic signature on a document through SignPact is to be given the same legal effect as a handwritten signature, to the fullest extent permitted by applicable law.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>6.2. User Responsibility:</strong> While SignPact's e-signature process is designed to comply with legal standards, it is ultimately <strong style={{fontWeight: 500}}>your responsibility</strong> to ensure that a particular document and the circumstances of its signing meet the requirements for enforceability.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>6.3. Audit Trail and Records:</strong> SignPact provides an audit trail for each electronic signature transaction, which may include details like the signers' names, email addresses, IP addresses (when available), and timestamps of when each person viewed and signed the document.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              7. Fees and Payments
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <em>Note: As of the date of these Terms, SignPact may still be in a beta or free trial stage. If we introduce paid plans or fees in the future, the following terms would apply.</em>
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>7.1. Pricing Plans:</strong> Certain features or usage of SignPact might require payment. We may offer various subscription plans (e.g., a free tier with limited features, and paid tiers with premium features or higher usage limits).
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>7.2. Payment Terms:</strong> Fees may be charged on a recurring basis (e.g., monthly or annually for subscriptions) or per use (e.g., a fee per document or per signature, if applicable).
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              8. Copyright and DMCA Policy
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              SignPact respects intellectual property rights and expects our users to do the same. If you believe that any content on the Service violates your copyright or other intellectual property rights, you can report it.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>8.1. DMCA Notice (for Copyright Infringement):</strong> If you are a copyright owner or an agent thereof, and you believe that any content hosted on SignPact infringes your copyrights, you may submit a notification pursuant to the Digital Millennium Copyright Act (DMCA).
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              9. Disclaimers of Warranties
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>SignPact is provided "AS IS" and "AS AVAILABLE."</strong> While we strive to provide a high-quality and reliable service, we need to make the following disclaimers:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>9.1. No Warranty of Error-Free Operation:</strong> We do not guarantee that the Service will always be available, uninterrupted, or free of errors, bugs, or security vulnerabilities.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>9.2. Quality and Accuracy:</strong> SignPact provides tools (including AI-based features) to help you with contracts, but we make no warranty that the results provided by the Service are accurate, complete, or suitable for your purposes.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>9.3. No Implied Warranties:</strong> To the fullest extent permitted by law, we expressly <strong style={{fontWeight: 500}}>disclaim all warranties of any kind</strong>, whether express, implied, or statutory.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              10. Limitation of Liability
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>To the maximum extent permitted by law, SignPact's liability to you is limited.</strong> By using the Service, you acknowledge and agree to the following limits on our liability:
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>10.1. Exclusion of Certain Damages:</strong> SignPact and its founders, owners, employees, agents, partners, and licensors will <strong style={{fontWeight: 500}}>not be liable to you for any indirect, incidental, special, consequential, or punitive damages</strong>.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>10.2. Cap on Liability:</strong> In no event will SignPact's total, cumulative liability to you for all claims arising out of or relating to these Terms or the Service <strong style={{fontWeight: 500}}>exceed the amount you have paid to SignPact for the Service in the twelve (12) months immediately preceding the event that gave rise to the claim</strong>, or <strong style={{fontWeight: 500}}>one hundred U.S. dollars (US $100)</strong>, whichever is greater.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              11. Indemnification
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              You agree to defend, indemnify, and hold harmless SignPact and its owners, officers, employees, contractors, agents, affiliates, and partners from and against <strong style={{fontWeight: 500}}>any and all claims, liabilities, damages, losses, and expenses</strong> (including reasonable attorneys' fees and costs) that arise out of or relate to any of the following:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>11.1. Your Use of the Service:</strong> Any use of the Service by you or through your account, including any actions taken by persons to whom you have given access to your account.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>11.2. Your User Content:</strong> Any claim that your User Content infringes or misappropriates any intellectual property or proprietary rights of a third party, violates any privacy rights or rights of publicity of a third party, is defamatory, or causes harm to a third party.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              12. Termination and Suspension
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>12.1. Voluntary Termination by You:</strong> You are free to stop using SignPact at any time. If you wish to delete your SignPact account, you may do so by contacting us at founder@signpact.ai or using any account deletion feature we provide in the interface.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>12.2. Termination or Suspension by SignPact:</strong> We reserve the right to suspend or terminate your access to the Service (or certain features of the Service) at any time, with or without notice, and with or without cause.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              13. Governing Law and Dispute Resolution
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>13.1. Governing Law:</strong> These Terms and any dispute or claim arising out of or in connection with them or the use of the Service shall be governed by and construed in accordance with the laws of the <strong style={{fontWeight: 500}}>United States</strong> and the laws of the <strong style={{fontWeight: 500}}>State of Delaware</strong>, without giving effect to any conflict of law principles that would result in the application of the laws of a different jurisdiction.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>13.2. Arbitration Agreement:</strong> <strong style={{fontWeight: 500}}>You and SignPact agree that any dispute, claim, or controversy arising out of or relating to these Terms or the use of the Service shall be resolved through final and binding arbitration, rather than in court</strong>, except as set forth under "Exceptions to Agreement to Arbitrate" below.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              14. Miscellaneous
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>14.1. Entire Agreement:</strong> These Terms (including any documents incorporated by reference, such as our Privacy Policy, and any specific terms for certain features or plans) constitute the <strong style={{fontWeight: 500}}>entire agreement</strong> between you and SignPact regarding the Service, and supersede all prior and contemporaneous agreements, proposals, or representations, written or oral, concerning its subject matter.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>14.2. Amendments:</strong> As noted, we may update these Terms from time to time. Except as explicitly provided in a written agreement signed by both parties, no amendment to these Terms will be binding unless posted or made by us.
            </p>
            
            <p className="mb-4" style={fontStyles.body}>
              <strong style={{fontWeight: 500}}>14.3. No Waiver:</strong> Our failure or delay in exercising any right, power, or privilege under these Terms is not a waiver of that right or provision. Similarly, a single or partial exercise of a right or power does not preclude further exercise of that or any other right.
            </p>
            
            <h2 className="text-2xl mb-4" style={fontStyles.subheading}>
              15. Contact Information
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              If you have any questions about these Terms, the practices of the Service, or if you need to provide any notices under these Terms, please contact us:
            </p>
            
            <ul className="list-disc pl-6 mb-8 space-y-2" style={fontStyles.body}>
              <li><strong style={{fontWeight: 500}}>Email:</strong> founder@signpact.ai</li>
              <li><strong style={{fontWeight: 500}}>Phone:</strong> (603) 322-0131</li>
              <li><strong style={{fontWeight: 500}}>Mail:</strong> SignPact, Inc., 1349 Hinman, Hanover, NH 03755, US</li>
            </ul>
            
            <p className="mb-4" style={fontStyles.body}>
              Thank you for reading our Terms of Service. We value your trust and aim to provide a service that helps you manage and sign contracts efficiently and securely. Happy contracting with SignPact!
            </p>
          </div>
        </div>
      </div>
      </motion.div>
      
      {/* Section Divider - Rail-style horizontal lines */}
      <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Gap between double rails */}
        <div 
          className="w-full"
          style={{
            height: '8px',
            background: 'transparent'
          }}
        />
        {/* Double rail effect - second line */}
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div>
      
      {/* Footer */}
      <ResponsiveFooter activePage="terms" />
    </div>
  );
};

export default TermsOfService; 